import { useStore } from '@nanostores/react';
import * as DropdownMenu from '@radix-ui/react-dropdown-menu';
import { memo } from 'react';
import { KeyboardShortcut } from '~/components/chat/commands/KeyboardShortcut';
import { commandStore } from '~/lib/stores/command';
import type { CommandActionContext, CommandShortcut } from '~/lib/stores/command/types';

interface KeyboardShortcutDropdownProps {
  uncontrolled?: boolean;
  shortcuts: CommandShortcut[];
  commandContext: CommandActionContext;
  children: React.ReactNode;
}

const KeyboardShortcutDropdown = memo(
  ({ uncontrolled, shortcuts, commandContext, children }: KeyboardShortcutDropdownProps) => {
    const isShortcutsDropdownOpen = useStore(commandStore.$isShortcutsDropdownOpen);

    if (!shortcuts.length) {
      return null;
    }

    return (
      <>
        <DropdownMenu.Root
          open={uncontrolled ? undefined : isShortcutsDropdownOpen}
          onOpenChange={(value) => {
            if (uncontrolled) {
              return;
            }

            requestAnimationFrame(() => {
              commandStore.$isShortcutsDropdownOpen.set(value);
            });
          }}
        >
          <DropdownMenu.Trigger asChild>{children}</DropdownMenu.Trigger>

          <DropdownMenu.Content
            align="end"
            side="top"
            onEscapeKeyDown={() => {
              requestAnimationFrame(() => {
                commandStore.$isShortcutsDropdownOpen.set(false);
              });
            }}
            className="bg-bolt-elements-background-depth-3 p-1.5 rounded-lg grid gap-0 border border-bolt-elements-borderColor shadow-lg shadow-black/20 animate-fade-in animate-duration-100"
            sideOffset={9}
          >
            {shortcuts.map((shortcut) => (
              <DropdownMenu.Item
                className="flex items-center cursor-pointer data-[highlighted]:bg-bolt-elements-item-backgroundActive outline-none p-1.5 pl-2 rounded"
                onClick={(event) => {
                  event.stopPropagation();
                  shortcut.action(commandContext);
                }}
                key={shortcut.label}
              >
                <span className="text-bolt-ds-textPrimary text-xs font-medium">{shortcut.label}</span>
                <div className="ml-auto pl-5 text-mauve11 group-data-[disabled]:text-mauve8 group-data-[highlighted]:text-white">
                  <KeyboardShortcut keys={shortcut.keys} />
                </div>
              </DropdownMenu.Item>
            ))}
          </DropdownMenu.Content>
        </DropdownMenu.Root>

        {shortcuts.map((shortcut) => (
          <KeyboardShortcut
            key={shortcut.label}
            keys={shortcut.keys}
            action={() => shortcut.action(commandContext)}
            hidden
          />
        ))}
      </>
    );
  },
);

KeyboardShortcutDropdown.displayName = 'KeyboardShortcutDropdown';

export { KeyboardShortcutDropdown };
